/**
 * Reply languages for AI Reply actions: the language a reply uses when the
 * customer's language cannot be detected. The wizard's own copy of the
 * extension's translation list and label rule (toolbar
 * `extension/src/conversations/translationService.js` `COMMON_TARGET_LANGUAGES`
 * / `MORE_TARGET_LANGUAGES`, `translateLanguageOptions.js`
 * `languageOptionLabel()`); the wizard backend (`utils/reply-languages.ts`)
 * and the toolbar admin API keep their own copies. Change all of them together.
 */
export const COMMON_REPLY_LANGUAGES: readonly string[] = [
  'en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'ru', 'ar', 'he',
  'hi', 'zh', 'ja', 'ko', 'tr', 'pl', 'sv', 'uk',
];

export const MORE_REPLY_LANGUAGES: readonly string[] = [
  'af', 'sq', 'am', 'hy', 'az', 'eu', 'be', 'bn', 'bs', 'bg',
  'ca', 'ceb', 'ny', 'co', 'hr', 'cs', 'da', 'et', 'fi', 'gl',
  'ka', 'el', 'gu', 'ht', 'ha', 'haw', 'hmn', 'hu', 'is', 'ig',
  'id', 'ga', 'jv', 'kn', 'kk', 'km', 'rw', 'ku', 'ky', 'lo',
  'la', 'lv', 'lt', 'lb', 'mk', 'mg', 'ms', 'ml', 'mt', 'mi',
  'mr', 'mn', 'my', 'ne', 'no', 'or', 'ps', 'fa', 'pa', 'ro',
  'sm', 'gd', 'sr', 'st', 'sn', 'sd', 'si', 'sk', 'sl', 'so',
  'su', 'sw', 'tg', 'ta', 'tt', 'te', 'th', 'tk', 'ur', 'ug',
  'uz', 'vi', 'cy', 'xh', 'yi', 'yo', 'zu',
];

export const DEFAULT_REPLY_LANGUAGE = 'en';

export function isReplyLanguage(code: string): boolean {
  return COMMON_REPLY_LANGUAGES.includes(code) || MORE_REPLY_LANGUAGES.includes(code);
}

function displayName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(code) || code;
  } catch {
    return code;
  }
}

/** The language's own name, then the English name when they differ: "עברית · Hebrew", "English". */
export function replyLanguageLabel(code: string): string {
  const own = displayName(code, code);
  const eng = displayName(code, 'en');
  return own && eng && own !== eng ? `${own} · ${eng}` : (own || eng || code);
}

export interface ReplyLanguageOption {
  code: string;
  label: string;
}

let optionGroups: { common: ReplyLanguageOption[]; more: ReplyLanguageOption[] } | null = null;

/**
 * Select options, built once: the common languages in list order, then the
 * rest sorted by English name, as the extension's picker lists them.
 */
export function replyLanguageOptionGroups(): { common: ReplyLanguageOption[]; more: ReplyLanguageOption[] } {
  if (!optionGroups) {
    const toOption = (code: string) => ({ code, label: replyLanguageLabel(code) });
    const english = new Map(MORE_REPLY_LANGUAGES.map((code) => [code, displayName(code, 'en')]));
    optionGroups = {
      common: COMMON_REPLY_LANGUAGES.map(toOption),
      more: [...MORE_REPLY_LANGUAGES]
        .sort((a, b) => (english.get(a) || a).localeCompare(english.get(b) || b))
        .map(toOption),
    };
  }
  return optionGroups;
}
